import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  SystemReviewDetail,
  SystemReviewFieldKey,
  SystemReviewFieldPreviousReview,
  SystemReviewPayload,
  SystemReviewRole,
  SystemReviewRoleBack,
  SystemReviewState,
  SystemReviewUserData,
  systemReviewBackendSelectedRoleMap,
  systemReviewFieldKeys,
  systemReviewReviewerLabels,
} from '@/app/interfaces/system-review';
import { SystemsService } from '@/app/services/ia-services/systems.service';
import { EconomicAmountPipe } from '@/app/pipe/economicAmount.pipe';
import { RiskLevelClassPipe } from '@/app/pipe/riskLevelClass.pipe';
import { PillComponent } from '@/app/shared/pill/pill.component';
import { ProjectCardComponent } from '@/app/shared/cards/project-card/project-card.component';
import { DesyAngularModule } from 'desy-angular';
import { getTaskFunctionLabel as mapTaskFunctionLabel } from '@/app/shared/mappers/task-function-label.mapper';
import { mapRiskLevelLabel } from '@/app/shared/mappers/risk-level-label.mapper';
import { mapSystemTypeLabel } from '@/app/shared/mappers/system-type-label.mapper';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipComponent } from '@/app/shared/tooltip/tooltip.component';
import {
  ReviewComment,
  ReviewCommentsBlockComponent,
} from '@/app/shared/review-comments-block/review-comments-block.component';
import { environment } from '@/environments/environment';

@Component({
  selector: 'app-system-review',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonsComponent,
    ConfirmDialogModule,
    DesyAngularModule,
    EconomicAmountPipe,
    PillComponent,
    ProjectCardComponent,
    ReviewCommentsBlockComponent,
    RiskLevelClassPipe,
    TooltipComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './system-review.component.html',
  styleUrl: './system-review.component.scss',
})
export class SystemReviewComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly systemsService = inject(SystemsService);
  private readonly location = inject(Location);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);

  systemId = '';
  userRoles: SystemReviewRole[] = [];
  activeUserRole: SystemReviewRole | null = null;
  activeBackendRole: SystemReviewRoleBack | null = null;
  systemReviewDetail: SystemReviewDetail | null = null;
  loadingReviewDetail = false;
  reviewDetailLoadAttempted = false;
  reviewDetailLoadError = false;
  readonlyReview = false;
  reviewComment = '';
  reviewComments: ReviewComment[] = [];
  selectedState: SystemReviewState | null = null;
  camposSeleccionados: SystemReviewFieldKey[] = [];
  private previousReviewState: Partial<Record<SystemReviewFieldKey, SystemReviewFieldPreviousReview>> = {};

  async ngOnInit(): Promise<void> {
    this.systemId = this.route.snapshot.paramMap.get('id') ?? '';
    await this.loadReviewDetail();
  }

  private async loadReviewDetail(): Promise<void> {
    if (!this.systemId) {
      return;
    }

    this.loadingReviewDetail = true;
    this.reviewDetailLoadAttempted = true;
    this.reviewDetailLoadError = false;

    try {
      const initialDetail = (await this.systemsService.getSystemDetails(this.systemId, 'system')) as SystemReviewDetail;

      if (initialDetail.state?.toUpperCase() === 'APPROVED') {
        await this.router.navigate(['/catalogo-ia/sistemas-de-ia/detalles', this.systemId]);
        return;
      }

      this.userRoles = this.getUserRolesForSystem(initialDetail);
      this.activeUserRole = this.resolveActiveUserRole(initialDetail, this.userRoles);
      this.activeBackendRole = this.activeUserRole ? this.mapUserRoleToBackend(this.activeUserRole) : null;

      const detail = this.activeBackendRole
        ? await this.systemsService.getSystemReviewDetail(this.systemId, this.activeBackendRole)
        : initialDetail;

      this.systemReviewDetail = detail;
      this.previousReviewState = this.buildPreviousReviewState(detail);
      this.readonlyReview = this.shouldOpenAsReadonlyReview();
      this.camposSeleccionados = this.shouldLoadPreviousFieldsAsCurrentSelection()
        ? this.getPreviousReviewedFieldKeys()
        : [];

      if (this.isSystemCorrectionState() && !this.isCurrentReviewRegistered()) {
        await this.router.navigate(['/catalogo-ia/sistemas-de-ia/detalles', this.systemId]);
        return;
      }

      this.reviewComments = this.buildReviewComments(detail);
    } catch {
      this.systemReviewDetail = null;
      this.userRoles = [];
      this.activeUserRole = null;
      this.activeBackendRole = null;
      this.camposSeleccionados = [];
      this.reviewDetailLoadError = true;
    } finally {
      this.loadingReviewDetail = false;
    }
  }

  backPage(): void {
    this.location.back();
  }

  isChief(): boolean {
    return this.activeBackendRole === 'JS';
  }

  isSupportGroup(): boolean {
    return this.userRoles.includes('apoyo_OGIA');
  }

  get shouldShowReviewComments(): boolean {
    return this.reviewComments.length > 0 && (!this.isSupportGroup() || this.readonlyReview);
  }

  isFieldSelected(fieldKey: SystemReviewFieldKey): boolean {
    return this.camposSeleccionados.includes(fieldKey);
  }

  toggleReviewField(fieldKey: SystemReviewFieldKey): void {
    if (this.readonlyReview) {
      return;
    }

    if (this.isFieldSelected(fieldKey)) {
      this.camposSeleccionados = this.camposSeleccionados.filter((selectedField) => selectedField !== fieldKey);
      return;
    }

    this.camposSeleccionados = [...this.camposSeleccionados, fieldKey];
  }

  hasPreviousReview(fieldKey: SystemReviewFieldKey): boolean {
    return !!this.previousReviewState[fieldKey]?.reviewers?.length;
  }

  previousReviewText(fieldKey: SystemReviewFieldKey): string {
    const reviewers = this.previousReviewState[fieldKey]?.reviewers ?? [];

    if (!reviewers.length) {
      return '';
    }

    return `Campo marcado por: ${reviewers.map((code) => systemReviewReviewerLabels[code] ?? code).join(', ')}`;
  }

  shouldShowPreviousReviewIndicators(): boolean {
    if (this.readonlyReview) {
      return true;
    }

    if (!this.isChief()) {
      return false;
    }

    return !this.readonlyReview;
  }

  confirm(state: SystemReviewState): void {
    if (this.readonlyReview) {
      return;
    }

    if (state === 'APPROVED' && this.isChief() && this.camposSeleccionados.length > 0) {
      return;
    }

    this.selectedState = state;

    this.confirmationService.confirm({
      message: '¿Seguro que desea finalizar revisión?',
      header: 'Confirmación',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Guardar',
      },
      accept: () => {
        void this.onSave();
      },
    });
  }

  async onSave(): Promise<void> {
    if (!this.selectedState || !this.activeBackendRole || !this.systemReviewDetail || !this.systemId) {
      return;
    }

    const payload: SystemReviewPayload = {
      idSistema: this.systemId,
      nombreSistema: this.systemReviewDetail.systemName ?? '',
      userRole: this.activeBackendRole,
      state: this.selectedState,
      comentario: this.reviewComment,
      camposSeleccionados: this.camposSeleccionados,
    };

    await this.systemsService.changeSystemReviewState(payload);
    await this.router.navigate(['/catalogo-ia/sistemas-de-ia/listado-sistemas']);
  }

  getSystemTypeLabel(value: string | null | undefined): string {
    return value ? mapSystemTypeLabel(value) : '-';
  }

  getFunctionLabel(value: string | null | undefined): string {
    return value ? mapTaskFunctionLabel(value) : '-';
  }

  getRiskLevelLabel(value: string | null | undefined): string {
    return mapRiskLevelLabel(value) || '-';
  }

  formatContractValidityDate(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const dateValue = value.toString();

    if (dateValue.length !== 8) {
      return dateValue;
    }

    return `${dateValue.substring(6, 8)}/${dateValue.substring(4, 6)}/${dateValue.substring(0, 4)}`;
  }

  private isSystemCorrectionState(): boolean {
    return this.systemReviewDetail?.state?.toUpperCase() === 'CORRECTION';
  }

  private getCurrentReviewState(): string {
    if (!this.systemReviewDetail || !this.activeUserRole) {
      return '';
    }

    return this.getReviewStateForUserRole(this.systemReviewDetail, this.activeUserRole);
  }

  private isCurrentReviewRegistered(): boolean {
    const currentState = this.getCurrentReviewState();

    return currentState === 'APPROVED' || currentState === 'CORRECTION';
  }

  private shouldOpenAsReadonlyReview(): boolean {
    if (!this.isCurrentReviewRegistered()) {
      return false;
    }

    return !this.isChief() || this.isSystemCorrectionState();
  }

  private shouldLoadPreviousFieldsAsCurrentSelection(): boolean {
    return this.isChief() && !this.readonlyReview;
  }

  private getPreviousReviewedFieldKeys(): SystemReviewFieldKey[] {
    return systemReviewFieldKeys.filter((fieldKey) => this.hasPreviousReview(fieldKey));
  }

  private getUserRolesForSystem(detail: SystemReviewDetail): SystemReviewRole[] {
    const userData = this.getStoredUserData();
    const systemOrganizations = [detail.codigo_organismo, detail.orgCodeCreator]
      .filter((org): org is string => !!org)
      .map((org) => this.normalizeOrgCodeForEnvironment(org));

    if (!systemOrganizations.length) {
      return [];
    }

    return Object.entries(userData.roles ?? {})
      .filter(
        ([, orgs]) =>
          Array.isArray(orgs) &&
          orgs.some((org) => systemOrganizations.includes(this.normalizeOrgCodeForEnvironment(org)))
      )
      .map(([role]) => role)
      .filter((role): role is SystemReviewRole => this.isSystemReviewRole(role));
  }

  private resolveActiveUserRole(detail: SystemReviewDetail, userRoles: SystemReviewRole[]): SystemReviewRole | null {
    const pendingRole = this.findPendingUserRole(detail, userRoles);

    return pendingRole ?? this.findRegisteredUserRole(detail, userRoles);
  }

  private findPendingUserRole(detail: SystemReviewDetail, userRoles: SystemReviewRole[]): SystemReviewRole | null {
    if (this.normalizeReviewState(detail.supportGroupState) === 'PENDING') {
      return userRoles.includes('apoyo_OGIA') ? 'apoyo_OGIA' : null;
    }

    const reviewerRole = this.getReviewerRolesInWorkflowOrder(userRoles).find(
      (role) => this.getReviewStateForUserRole(detail, role) === 'PENDING'
    );

    if (reviewerRole) {
      return reviewerRole;
    }

    if (this.normalizeReviewState(detail.serviceManagerState) === 'PENDING') {
      return userRoles.includes('jefe_servicio_OGIA') ? 'jefe_servicio_OGIA' : null;
    }

    return null;
  }

  private findRegisteredUserRole(detail: SystemReviewDetail, userRoles: SystemReviewRole[]): SystemReviewRole | null {
    return (
      userRoles.find((role) => {
        const state = this.getReviewStateForUserRole(detail, role);

        return state === 'APPROVED' || state === 'CORRECTION';
      }) ?? null
    );
  }

  private getReviewerRolesInWorkflowOrder(userRoles: SystemReviewRole[]): SystemReviewRole[] {
    const reviewerRoles: SystemReviewRole[] = ['funcional_OGIA', 'juridico_OGIA', 'tecnico_OGIA', 'dpd_OGIA'];

    return reviewerRoles.filter((role) => userRoles.includes(role));
  }

  private getReviewStateForUserRole(detail: SystemReviewDetail, role: SystemReviewRole): string {
    const stateByRole: Record<SystemReviewRole, string | null | undefined> = {
      apoyo_OGIA: detail.supportGroupState,
      funcional_OGIA: detail.functionalReviewerState,
      juridico_OGIA: detail.legalReviewerState,
      tecnico_OGIA: detail.technicalReviewerState,
      dpd_OGIA: detail.dpdReviewerState,
      jefe_servicio_OGIA: detail.serviceManagerState,
    };

    return this.normalizeReviewState(stateByRole[role]);
  }

  private mapUserRoleToBackend(userRole: SystemReviewRole): SystemReviewRoleBack | null {
    switch (userRole) {
      case 'apoyo_OGIA':
        return 'GA';
      case 'funcional_OGIA':
        return 'RF';
      case 'juridico_OGIA':
        return 'RJ';
      case 'tecnico_OGIA':
        return 'RT';
      case 'dpd_OGIA':
        return 'RDPD';
      case 'jefe_servicio_OGIA':
        return 'JS';
      default:
        return null;
    }
  }

  private normalizeReviewState(state?: string | null): string {
    return (state ?? 'PENDING').toUpperCase();
  }

  private normalizeOrgCodeForEnvironment(orgCode: string): string {
    return orgCode.startsWith(environment.environment) ? orgCode : `${environment.environment}${orgCode}`;
  }

  private getStoredUserData(): SystemReviewUserData {
    const storedUserData = localStorage.getItem('userData');

    if (!storedUserData) {
      return {};
    }

    return JSON.parse(storedUserData) as SystemReviewUserData;
  }

  private isSystemReviewRole(role: string): role is SystemReviewRole {
    return (
      role === 'apoyo_OGIA' ||
      role === 'funcional_OGIA' ||
      role === 'juridico_OGIA' ||
      role === 'tecnico_OGIA' ||
      role === 'dpd_OGIA' ||
      role === 'jefe_servicio_OGIA'
    );
  }

  private buildReviewComments(detail: SystemReviewDetail): ReviewComment[] {
    if (this.activeBackendRole === 'GA' && this.readonlyReview) {
      return [
        {
          userRole: 'GA',
          comment: detail.commentSupportGroup ?? null,
          state: detail.supportGroupState ?? null,
        },
      ];
    }

    const reviewComments: ReviewComment[] = [
      {
        userRole: 'GA',
        comment: detail.commentSupportGroup ?? null,
        state: detail.supportGroupState ?? null,
      },
      {
        userRole: 'RF',
        comment: detail.commentRevisorFuncional ?? null,
        state: detail.functionalReviewerState ?? null,
      },
      {
        userRole: 'RJ',
        comment: detail.commentRevisorJuridico ?? null,
        state: detail.legalReviewerState ?? null,
      },
      {
        userRole: 'RT',
        comment: detail.commentRevisorTecnico ?? null,
        state: detail.technicalReviewerState ?? null,
      },
      {
        userRole: 'RDPD',
        comment: detail.commentRevisorDpd ?? null,
        state: detail.dpdReviewerState ?? null,
      },
    ];

    if (this.isChief()) {
      reviewComments.push({
        userRole: 'JS',
        comment: detail.commentServiceManager ?? null,
        state: detail.serviceManagerState ?? null,
      });
    }

    return reviewComments;
  }

  private buildPreviousReviewState(
    data: unknown
  ): Partial<Record<SystemReviewFieldKey, SystemReviewFieldPreviousReview>> {
    const previousReviewState: Partial<Record<SystemReviewFieldKey, SystemReviewFieldPreviousReview>> = {};

    Object.entries(systemReviewBackendSelectedRoleMap).forEach(([backendKey, roleCode]) => {
      this.extractPreviousReviewedFields(data, backendKey).forEach((fieldKey) => {
        this.addPreviousReviewRole(previousReviewState, fieldKey, roleCode);
      });
    });

    return previousReviewState;
  }

  private extractPreviousReviewedFields(data: unknown, backendKey: string): SystemReviewFieldKey[] {
    if (!data || typeof data !== 'object') {
      return [];
    }

    const selectedFields = (data as Record<string, unknown>)[backendKey];

    if (!Array.isArray(selectedFields)) {
      return [];
    }

    return selectedFields.filter((fieldName): fieldName is SystemReviewFieldKey => this.isReviewedField(fieldName));
  }

  private isReviewedField(fieldName: unknown): fieldName is SystemReviewFieldKey {
    return typeof fieldName === 'string' && systemReviewFieldKeys.includes(fieldName as SystemReviewFieldKey);
  }

  private addPreviousReviewRole(
    previousReviewState: Partial<Record<SystemReviewFieldKey, SystemReviewFieldPreviousReview>>,
    fieldKey: SystemReviewFieldKey,
    roleCode: string
  ): void {
    const reviewers = previousReviewState[fieldKey]?.reviewers ?? [];

    if (reviewers.includes(roleCode)) {
      return;
    }

    previousReviewState[fieldKey] = {
      reviewers: [...reviewers, roleCode],
    };
  }
}
